/**
 * balanceAnalyzer.ts — Live Balance Math
 *
 * Computes TTK, DPS, and an overall balance score from current game state.
 * Pure calculation — no rendering, no mutation.
 */

import type { GameState } from './types';
import { ENEMY_DATABASE, getScaledStats } from './enemies';
import { getWeaponSystemState } from './weaponAdapter';
import { getWeaponDef, computeWeaponStats } from './weapons';
import { getAppliedBuffsList, getPercContributions } from './itemsAdapter';

// ─── Colour palette per stat (used by balance panel bar segments) ────────────
export const STAT_COLORS: Record<string, string> = {
  maxHp:              '#39ff14', // green
  damage:             '#ff8800', // orange
  damageBonus:        '#ff8800',
  moveSpeed:          '#ffcc00', // yellow
  attackSpeedBonus:   '#ffee44',
  critChance:         '#00ffff', // cyan
  critDamage:         '#00ddff',
  armor:              '#aaaaaa', // silver
  maxShield:          '#4488ff', // blue
  shield:             '#4488ff',
  lifesteal:          '#ff4444', // red
  hpRegen:            '#ff88cc', // pink
  evasion:            '#cc44ff', // purple
  knockbackBonus:     '#ffaa00', // amber
  xpMultiplier:       '#44ffaa', // mint
  goldMultiplier:     '#ffdd00', // gold
  rangeBonus:         '#88ccff', // sky blue
  pickupRange:        '#00ffcc', // teal
  luck:               '#ffffff', // white
  thorns:             '#ff6644', // burnt orange
};

export const STAT_LABELS: Record<string, string> = {
  maxHp:              'MAX HP',
  damage:             'DAMAGE',
  damageBonus:        'DAMAGE BONUS',
  moveSpeed:          'MOVE SPEED',
  attackSpeedBonus:   'ATTACK SPEED',
  critChance:         'CRIT CHANCE',
  critDamage:         'CRIT DAMAGE',
  armor:              'ARMOR',
  maxShield:          'SHIELD',
  lifesteal:          'LIFESTEAL',
  hpRegen:            'HP REGEN/s',
  evasion:            'EVASION',
  knockbackBonus:     'KNOCKBACK',
  xpMultiplier:       'XP MULT',
  goldMultiplier:     'GOLD MULT',
  rangeBonus:         'RANGE',
  pickupRange:        'PICKUP RANGE',
  luck:               'LUCK',
  thorns:             'THORNS',
};

// ─── Data Structures ─────────────────────────────────────────────────────────

export interface EnemyThreat {
  id: string;
  name: string;
  scaledHP: number;
  dpsToPlayer: number;    // effective DPS this enemy deals to the player right now
  ttk: number;            // seconds for this enemy to kill player (standing still)
  hitsToKill: number;     // how many touches to kill player
  hitInterval: number;    // real time between effective damage ticks
  spawnWeight: number;
}

export interface WeaponVsEnemy {
  weaponId: string;
  weaponName: string;
  enemyId: string;
  enemyName: string;
  dps: number;
  ttk: number;            // seconds for weapon to kill this enemy type
  shotsToKill: number;
}

export interface StatContribution {
  label: string;          // "Base" | "Shrine: +5 Armor" | "Iron Stomach Lv2"
  value: number;          // The numeric amount this source adds
  color: string;          // Colour for the bar segment
  isPct: boolean;
}

export interface StatBar {
  stat: string;
  label: string;
  unit: string;           // "" | "%" | "/s" etc.
  baseValue: number;
  totalValue: number;
  maxBarValue: number;    // what 100% bar width represents
  contributions: StatContribution[];
  color: string;          // primary stat color
}

/** Snapshot of one completed run */
export interface RunSnapshot {
  runNum: number;
  timestamp: number;
  timeSurvived: number;
  floor: number;
  killCount: number;
  finalLevel: number;
  percs: string[];
  weapons: string[];
  items: string[];
  balanceScore: number;
  avgPlayerTTK: number;
  avgEnemyTTK: number;
}

export interface BalanceReport {
  runTimeSeconds: number;

  // Combat analysis
  threats: EnemyThreat[];
  weaponVsEnemies: WeaponVsEnemy[];

  // Aggregate
  playerEffectiveHP: number;
  avgPlayerTTK: number;   // weighted average across enemy types
  avgEnemyTTK: number;    // avg time for best weapon to kill each enemy type

  // Balance score 0–100 (50 = balanced, >50 = OP, <50 = UP)
  balanceScore: number;
  balanceLabel: 'CRITICAL' | 'UNDERPOWERED' | 'BALANCED' | 'OVERPOWERED' | 'GOD MODE';
  balanceRatio: number;   // actual kills-per-death ratio used in formula

  // Stat bars with source breakdown
  statBars: StatBar[];

  // Kill rate
  killsPerMinute: number;
}

// ─── Target balance ratio ──────────────────────────────────────────────────
// Player should kill ~5× faster than enemies kill the player.
// Below 2 = underpowered, above 10 = god mode.
const TARGET_RATIO = 5.0;

// ─── Main computation ─────────────────────────────────────────────────────────

export function computeBalanceReport(state: GameState): BalanceReport {
  const p = state.player;
  const t = state.time;

  // ── Player effective HP ──────────────────────────────────────────
  const effectiveHP = Math.max(1, p.hp + (p.shield ?? 0));

  // ── Threats: one row per enemy type ──────────────────────────────
  const threats: EnemyThreat[] = Object.values(ENEMY_DATABASE)
    .filter(def => t >= def.minSpawnTime)
    .map(def => {
      const scaled = getScaledStats(def, t, 'normal');
      const armorReduction = p.armor ?? 0;
      const rawDmg = Math.max(1, scaled.damage - armorReduction);
      const evasionFactor = 1 - Math.min(0.9, (p.evasion ?? 0) / 100);
      const effectiveDmgPerHit = rawDmg * evasionFactor;

      // i-frames limit how often the enemy can actually land damage
      const hitInterval = Math.max(def.contactCooldown, def.iFrameGrant);
      const dpsToPlayer = effectiveDmgPerHit / hitInterval;
      const hitsToKill = Math.ceil(effectiveHP / effectiveDmgPerHit);
      const ttk = hitsToKill * hitInterval;

      return {
        id: def.id,
        name: def.name,
        scaledHP: scaled.hp,
        dpsToPlayer,
        ttk: Math.min(ttk, 9999),
        hitsToKill,
        hitInterval,
        spawnWeight: def.spawnWeight,
      } satisfies EnemyThreat;
    })
    .sort((a, b) => a.ttk - b.ttk); // shortest TTK first (most dangerous)

  // ── Weapon vs Enemy ──────────────────────────────────────────────
  const ws = getWeaponSystemState();
  const weaponVsEnemies: WeaponVsEnemy[] = [];

  if (ws) {
    for (const weapon of ws.weapons) {
      const def = getWeaponDef(weapon.definitionId);
      if (!def) continue;
      const stats = computeWeaponStats(def, weapon.level, weapon.rarity);

      // Effective damage per burst (with player bonuses)
      const dmgMult = 1 + Math.max(0, (p.damageBonus ?? 0) / 100);
      const baseDmg = stats.damage * dmgMult;
      const avgCritMult = 1 + stats.critChance * stats.critDamage;
      const dmgPerBurst = baseDmg * avgCritMult * stats.projectileCount;

      // Attack speed bonus
      const atkSpeedMult = 1 + Math.max(0, (p.attackSpeedBonus ?? 0) / 100);
      const effectiveCooldown = Math.max(0.05, stats.reloadTime / atkSpeedMult);
      const dps = dmgPerBurst / effectiveCooldown;

      for (const enemy of threats) {
        const shotsToKill = Math.ceil(enemy.scaledHP / (baseDmg * avgCritMult));
        const ttk = enemy.scaledHP / dps;

        weaponVsEnemies.push({
          weaponId: weapon.definitionId,
          weaponName: def.name,
          enemyId: enemy.id,
          enemyName: enemy.name,
          dps,
          ttk: Math.min(ttk, 9999),
          shotsToKill: Math.max(1, shotsToKill),
        });
      }
    }
  }

  // ── Aggregate TTK figures ────────────────────────────────────────
  const totalWeight = threats.reduce((s, e) => s + e.spawnWeight, 0) || 1;

  const avgPlayerTTK = threats.reduce((sum, e) => sum + e.ttk * e.spawnWeight, 0) / totalWeight;

  // Best weapon TTK per enemy (lowest TTK = fastest kill)
  let avgEnemyTTK = 0;
  for (const enemy of threats) {
    const byWeapon = weaponVsEnemies.filter(w => w.enemyId === enemy.id);
    const bestTTK = byWeapon.length > 0
      ? Math.min(...byWeapon.map(w => w.ttk))
      : 9999;
    avgEnemyTTK += bestTTK * enemy.spawnWeight;
  }
  avgEnemyTTK = avgEnemyTTK / totalWeight;

  // ── Balance score ────────────────────────────────────────────────
  const ratio = avgEnemyTTK > 0 ? avgPlayerTTK / avgEnemyTTK : 0;
  const rawScore = Math.min(100, (ratio / TARGET_RATIO) * 50);
  const balanceScore = Math.max(0, Math.round(rawScore));

  let balanceLabel: BalanceReport['balanceLabel'];
  if (balanceScore < 15)       balanceLabel = 'CRITICAL';
  else if (balanceScore < 35)  balanceLabel = 'UNDERPOWERED';
  else if (balanceScore < 65)  balanceLabel = 'BALANCED';
  else if (balanceScore < 85)  balanceLabel = 'OVERPOWERED';
  else                         balanceLabel = 'GOD MODE';

  // ── Stat bars with source breakdown ──────────────────────────────
  const statBars = buildStatBars(state);

  // ── Kill rate ────────────────────────────────────────────────────
  const killsPerMinute = t > 5 ? (state.killCount / t) * 60 : 0;

  return {
    runTimeSeconds: t,
    threats,
    weaponVsEnemies,
    playerEffectiveHP: effectiveHP,
    avgPlayerTTK,
    avgEnemyTTK,
    balanceScore,
    balanceLabel,
    balanceRatio: ratio,
    statBars,
    killsPerMinute,
  };
}

// ─── Stat bar builder ─────────────────────────────────────────────────────────

function buildStatBars(state: GameState): StatBar[] {
  const p = state.player;
  const bars: StatBar[] = [];

  // Gather shrine/chest buff sources
  const buffs = getAppliedBuffsList();
  // Gather perk sources
  const percContribList = getPercContributions();

  function buffContribs(stat: string): StatContribution[] {
    return buffs
      .filter(b => b.stat === stat && b.value !== 0)
      .map(b => ({
        label: b.label ?? `Item: ${b.stat}`,
        value: b.value,
        color: STAT_COLORS[stat] ?? '#ffffff',
        isPct: b.pct,
      }));
  }

  function percContribs(stat: string): StatContribution[] {
    return percContribList
      .filter(p => p.stat === stat && p.accumulatedBonus !== 0)
      .map(p => ({
        label: `${p.percName} Lv${p.level}`,
        value: p.accumulatedBonus,
        color: p.accentColor,
        isPct: p.isPercent,
      }));
  }

  function makeBar(
    stat: string, label: string, unit: string,
    baseValue: number, totalValue: number, maxBarValue: number,
  ): StatBar {
    const color = STAT_COLORS[stat] ?? '#ffffff';
    const contributions: StatContribution[] = [
      { label: 'Base', value: baseValue, color: 'rgba(255,255,255,0.25)', isPct: false },
      ...buffContribs(stat),
      ...percContribs(stat),
    ];
    return { stat, label, unit, baseValue, totalValue, maxBarValue, contributions, color };
  }

  // MAX HP
  bars.push(makeBar('maxHp', 'MAX HP', '', 100, p.maxHp, Math.max(200, p.maxHp * 1.2)));

  // CURRENT HP
  bars.push({
    stat: 'hp', label: 'CURRENT HP', unit: '', color: '#39ff14',
    baseValue: 0, totalValue: p.hp, maxBarValue: p.maxHp,
    contributions: [
      { label: 'Current', value: p.hp, color: p.hp / p.maxHp < 0.25 ? '#ff3333' : '#39ff14', isPct: false },
    ],
  });

  // SHIELD
  if (p.maxShield > 0) {
    bars.push(makeBar('maxShield', 'SHIELD', '', 0, p.maxShield, Math.max(60, p.maxShield * 1.2)));
  }

  // ARMOR
  if (p.armor > 0) {
    bars.push(makeBar('armor', 'ARMOR (flat dmg block)', '', 0, p.armor, Math.max(30, p.armor * 1.5)));
  }

  // LIFESTEAL
  if (p.lifesteal > 0) {
    bars.push(makeBar('lifesteal', 'LIFESTEAL', '%', 0, p.lifesteal, Math.max(30, p.lifesteal * 1.5)));
  }

  // HP REGEN
  if (p.hpRegen > 0) {
    bars.push(makeBar('hpRegen', 'HP REGEN', '/s', 0, p.hpRegen, Math.max(10, p.hpRegen * 1.5)));
  }

  // DAMAGE BONUS
  if (p.damageBonus > 0) {
    bars.push(makeBar('damageBonus', 'DAMAGE BONUS', '%', 0, p.damageBonus, Math.max(100, p.damageBonus * 1.5)));
  }

  // CRIT CHANCE
  bars.push(makeBar('critChance', 'CRIT CHANCE', '%', 5, p.critChance, Math.max(50, p.critChance * 1.5)));

  // EVASION
  if (p.evasion > 0) {
    bars.push(makeBar('evasion', 'EVASION', '%', 0, p.evasion, Math.max(50, p.evasion * 1.5)));
  }

  // MOVE SPEED (relative — base 100)
  bars.push(makeBar('moveSpeed', 'MOVE SPEED', '', 100, p.speed, 200));

  return bars;
}
